import JSZip = require("jszip/dist/jszip.min.js");

declare const Uppy: any;

export interface UploadedFile {

  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
}

export interface UppyConfigOptions {

  maxNumberOfFiles: number;
  endpoint: string;
  allowedFileTypes?: string[];
  locale?: { strings: {} }
}

export class UppyHelper {

    public uppyInstance: any;

    constructor(
        public target: string,
        public config: UppyConfigOptions,
        public onBeforeFileAdded: (file: File) => File,
        public onBeforeRequest: (req: any, file: any) => void,
        public onUploadComplete: (file: UploadedFile) => void,
        public onEachUploadError?: (file: UploadedFile | null, error: Error) => void, // optional callback
        public getFileTree?: (fileTree: String) => void,
    ){
        this.init();
    }

    public init () {

        if (!this.validateTargetElement()) return;

        this.createUppyInstance();
        this.addDashboardPlugin();
        this.addTusPlugin();
        this.addEventListeners();
    }

    private validateTargetElement(): boolean {

        if (!document.querySelector('#' + this.target)) {
            console.error('target', '#' + this.target, 'element not found');
            return false;
        }
        return true;
    }

    private createUppyInstance(): void {

        const rules: any = {
            maxNumberOfFiles: this.config.maxNumberOfFiles,
        };

        if (this.config.allowedFileTypes) {
            rules.allowedFileTypes = this.config.allowedFileTypes;
        }

        this.uppyInstance = new Uppy.Uppy({
            allowMultipleUploadBatches: this.config.maxNumberOfFiles > 1,
            restrictions: rules,
            onBeforeFileAdded: (file, files) => this.onBeforeFileAdded(file)
        });
    }

    private addDashboardPlugin(): void {

        const dashboardOptions: any = {
            inline: true,
            target: '#' + this.target,
            showProgressDetails: true
        };

        if (this.config.locale) {
            dashboardOptions.locale = this.config.locale;
        }

        this.uppyInstance.use(Uppy.Dashboard, dashboardOptions);
    }

    private addTusPlugin(): void {

        let tusEndpoint = this.config.endpoint;
        const self = this;

        this.uppyInstance.use(Uppy.Tus, {
            endpoint: tusEndpoint,
            onBeforeRequest: (req, file) => {self.onBeforeRequest(req, file)}
        });
    }

    private addEventListeners(): void {

        this.uppyInstance.on('complete', (result) => this.handleComplete(result));
        this.uppyInstance.on('upload-error', (file, error, response) => this.handleEachUploadError(file, error));
        this.uppyInstance.on('file-added', (file) => this.getContentTree(file));
    }

    public formatBytes(bytes: number): string {
        
        if (bytes < 1024) return `${bytes} B`;
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(2)} KB`;
        const mb = kb / 1024;
        if (mb < 1024) return `${mb.toFixed(2)} MB`;
        const gb = mb / 1024;
        return `${gb.toFixed(2)} GB`;
    }


    private handleComplete(result): void {

        result.successful.forEach((item) => {
            const file: UploadedFile = {
                id: item.id,
                name: item.name,
                type: item.type,
                size: item.size,
                url: item.uploadURL
            };
            this.onUploadComplete(file);
        });
    }

    private getContentTree(file) {
          // file.data is the File/Blob object
            const jszip = new JSZip();

           // this.readZip(file.data);
            file.data.arrayBuffer().then(arrayBuffer => {
                jszip.loadAsync(arrayBuffer).then(zip => {


                    let tree = "";
                    const jobs: Promise<string>[] = [];
                    // Iterate central directory entries
                    Object.keys(zip.files).forEach((entryName) => {
                        const entry = zip.files[entryName];
                        if (entry.dir) {
                            
                            tree += `[dir]${entryName}\n`;
                        } else {
                            
                            const job = entry.async('arraybuffer').then(buf => {
                                const size = (buf as ArrayBuffer).byteLength; // bytes
                                //console.log(`File: ${entryName}, Size: ${this.formatBytes(size)} (${size} bytes)`);
                                tree += `[file]${entryName} ( ${this.formatBytes(size)} )\n`;
                            }).catch(err => console.error('Size read error for', entryName, err));

                            jobs.push(job);
                        }
                    });
                    Promise.all(jobs).then(parts => {
                        tree += parts.join('');
                        this.getFileTree(tree);
                        //console.log('ASYNC Tree',tree);
                    });
                    
                }).catch(err => {
                console.error("Error reading zip:", err);
                });
            }).catch(err => {
                console.error("Error reading file as arrayBuffer:", err);
            });
    }

    private handleEachUploadError(file, error): void {

        console.error('Upload error for file:', file.name, error);
        if (this.onEachUploadError) {
            const errFile: UploadedFile | null = file
            ? {
                id: file.id,
                name: file.name,
                type: file.type,
                size: file.size,
                url: '',
            }
            : null;
            this.onEachUploadError(errFile, error);
            return;
        }
        alert(`Failed to upload ${file.name}: ${(error && error.message) || 'Unknown error'}`);
    }

     public preloadUploadedFile(file : UploadedFile) {

        const blob = new Blob(['dummy content'], {
            type: file.type || this.config.allowedFileTypes[0] 
        });

        this.uppyInstance.once('file-added', (addedFile) => {
            const now = Date.now();

            this.uppyInstance.setFileState(addedFile.id, {
                progress: {
                    uploadStarted: now,
                    uploadComplete: true,
                    percentage: 100,
                    bytesUploaded: blob.size,
                    bytesTotal: blob.size
                },
                response: {
                    status: 200,
                    uploadURL: file.url
                },
                isRestored: true
            });

            this.uppyInstance.emit('upload-success', addedFile, {
                status: 200,
                uploadURL: file.url
            });
        });

        this.uppyInstance.addFile({
            name: file.name,
            type: file.type || this.config.allowedFileTypes[0],
            size: file.size || blob.size,
            data: blob,
            isRestored: true,
            meta: { isPreloaded: true },
            source: 'CDN'
        });
    }

    public clearFiles() {

        this.uppyInstance.getFiles().forEach(file => this.clearFile(file));
    }

    public clearFile(file) {

        this.uppyInstance.removeFile(file.id);
    }

    public disableNewUpload() {
        this.uppyInstance.getPlugin('Dashboard').setOptions({ disabled: true });
    }

    public enableNewUpload() {
        this.uppyInstance.getPlugin('Dashboard').setOptions({ disabled: false });
    }
}


// WEBPACK FOOTER //
// ./js/utils/uppyHelper.ts